"use client";

import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  displayChatTitle,
  listChatHistory,
  hydrateChatHistoryFromServer,
  removeChatHistory,
  renameChatHistory,
  setChatPinned,
  subscribeChatHistory,
  type ChatHistoryItem,
} from "@/features/chat/multi/history/chat-history";
import {
  clearPersistedBuildSession,
  persistLastFlowBuilderSessionId,
} from "@/features/chat/multi/persistence/clear-persisted-session";
import { ensureMultiplexConnection } from "@/features/chat/multi/transport/multiplex-stream";
import {
  getHubSubscribeStatus,
  subscribeHubStatus,
} from "@/features/chat/multi/transport/agent-runtime-stream";
import {
  emitOpenWorkflowCanvas,
  emitWorkspaceTitle,
  flowBuilderChatHref,
  onSessionUrl,
} from "@/shared/builder-session";
import { queryScrollAreaViewport } from "@/shared/ui/shadcn-components/scroll-area-viewport";

const CHAT_PAGE_SIZE = 20;

function sessionIdFromUrl() {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("sessionId");
}

export function useSidebarChats(collapsed: boolean) {
  const pathname = usePathname();
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [chats, setChats] = useState<ChatHistoryItem[]>([]);
  const [chatsReady, setChatsReady] = useState(false);
  const [visibleCount, setVisibleCount] = useState(CHAT_PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(() => sessionIdFromUrl());
  const [urlPathname, setUrlPathname] = useState(pathname);
  const [queryKey, setQueryKey] = useState(query);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const chatScrollAreaRef = useRef<HTMLDivElement | null>(null);
  const activeChatRef = useRef<HTMLButtonElement | null>(null);
  const wasReconnectingRef = useRef(false);

  if (pathname !== urlPathname) {
    setUrlPathname(pathname);
    setActiveSessionId(sessionIdFromUrl());
  }

  if (query !== queryKey) {
    setQueryKey(query);
    setVisibleCount(CHAT_PAGE_SIZE);
  }

  const pullServerChats = useCallback(() => {
    // Insights uses its own history store; FlowOps :3999 sessions hydrate is N/A here.
    if (pathname?.startsWith("/insights")) {
      setChats(listChatHistory());
      setChatsReady(true);
      return;
    }
    void hydrateChatHistoryFromServer()
      .then((items) => {
        setChats(items);
        setChatsReady(true);
      })
      .catch(() => undefined);
  }, [pathname]);

  useEffect(() => {
    void ensureMultiplexConnection();
    const refresh = () => {
      setChats(listChatHistory());
      setChatsReady(true);
    };
    const raf = window.requestAnimationFrame(() => {
      refresh();
      pullServerChats();
    });
    const unsubscribe = subscribeChatHistory(refresh);
    return () => {
      window.cancelAnimationFrame(raf);
      unsubscribe();
    };
  }, [pullServerChats]);

  // Agent was down → sessions list 502'd and sidebar stayed stale. Re-pull when
  // the multiplex hub recovers (or the tab is focused again).
  useEffect(() => {
    const onHub = () => {
      const status = getHubSubscribeStatus();
      if (status === "reconnecting") {
        wasReconnectingRef.current = true;
        return;
      }
      if (status === "connected" && wasReconnectingRef.current) {
        wasReconnectingRef.current = false;
        pullServerChats();
      }
    };
    const onFocus = () => pullServerChats();
    const unsubscribe = subscribeHubStatus(onHub);
    window.addEventListener("focus", onFocus);
    return () => {
      unsubscribe();
      window.removeEventListener("focus", onFocus);
    };
  }, [pullServerChats]);

  useEffect(() => {
    const sync = () => setActiveSessionId(sessionIdFromUrl());
    const unsubscribeSessionUrl = onSessionUrl((detail) => {
      if (detail?.sessionId) {
        setActiveSessionId(detail.sessionId);
        window.requestAnimationFrame(() => {
          activeChatRef.current?.scrollIntoView({ block: "nearest" });
        });
      } else {
        sync();
      }
    });
    window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener("popstate", sync);
      unsubscribeSessionUrl();
    };
  }, [pathname]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((chat) => displayChatTitle(chat).toLowerCase().includes(q));
  }, [chats, query]);

  const visibleChats = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMoreChats = visibleCount < filtered.length;

  const loadMoreChats = useCallback(() => {
    if (!hasMoreChats || loadingMore) return;
    setLoadingMore(true);
    window.setTimeout(() => {
      setVisibleCount((count) => count + CHAT_PAGE_SIZE);
      setLoadingMore(false);
    }, 180);
  }, [hasMoreChats, loadingMore]);

  // Scroll near bottom only — IntersectionObserver fired on mount when the sentinel
  // was already visible, which loaded every chat page in production in one burst.
  useEffect(() => {
    if (collapsed) return;

    let disposed = false;
    let teardown: (() => void) | undefined;

    const bind = () => {
      if (disposed) return true;
      const root = queryScrollAreaViewport(chatScrollAreaRef.current);
      if (!root) return false;

      const onScroll = () => {
        const distance = root.scrollHeight - root.scrollTop - root.clientHeight;
        if (hasMoreChats && distance < 160) loadMoreChats();
      };

      root.addEventListener("scroll", onScroll, { passive: true });
      teardown = () => root.removeEventListener("scroll", onScroll);
      return true;
    };

    if (!bind()) {
      const raf = window.requestAnimationFrame(() => {
        bind();
      });
      return () => {
        disposed = true;
        window.cancelAnimationFrame(raf);
        teardown?.();
      };
    }

    return () => {
      disposed = true;
      teardown?.();
    };
  }, [collapsed, hasMoreChats, loadMoreChats]);

  const startNewDraft = useCallback(() => {
    setActiveSessionId(null);
    clearPersistedBuildSession();
  }, []);

  const openChat = useCallback(
    (id: string) => {
      const sessionId = id.trim();
      if (!sessionId) return;
      setActiveSessionId(sessionId);
      persistLastFlowBuilderSessionId(sessionId);
      router.push(flowBuilderChatHref(sessionId));
    },
    [router],
  );

  const openWorkflowCanvas = useCallback(
    (id: string) => {
      const sessionId = id.trim();
      if (!sessionId) return;
      setActiveSessionId(sessionId);
      persistLastFlowBuilderSessionId(sessionId);
      if (pathname.startsWith("/flow-builder") && activeSessionId === sessionId) {
        emitOpenWorkflowCanvas();
        return;
      }
      router.push(`${flowBuilderChatHref(sessionId)}&canvas=1`);
    },
    [activeSessionId, pathname, router],
  );

  const pinChat = useCallback((chat: ChatHistoryItem) => {
    setChatPinned(chat.id, !chat.pinned);
  }, []);

  const renameChat = useCallback(
    (chat: ChatHistoryItem) => {
      const title = window.prompt("Rename chat", displayChatTitle(chat));
      const next = title?.trim();
      if (!next) return;
      renameChatHistory(chat.id, next);
      if (activeSessionId === chat.id) emitWorkspaceTitle(chat.id, next);
    },
    [activeSessionId],
  );

  const shareChat = useCallback(async (chat: ChatHistoryItem) => {
    const url = `${window.location.origin}/flow-builder?sessionId=${encodeURIComponent(chat.id)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt("Copy chat link", url);
    }
  }, []);

  const deleteChat = useCallback(
    (chat: ChatHistoryItem) => {
      if (!window.confirm(`Delete "${displayChatTitle(chat)}"? This cannot be undone.`)) return;
      removeChatHistory(chat.id);
      if (activeSessionId === chat.id) {
        setActiveSessionId(null);
        clearPersistedBuildSession();
        router.push("/home");
      }
    },
    [activeSessionId, router],
  );

  return {
    pathname,
    query,
    setQuery,
    chatsReady,
    filtered,
    visibleChats,
    hasMoreChats,
    loadingMore,
    activeSessionId,
    loadMoreRef,
    chatScrollAreaRef,
    activeChatRef,
    startNewDraft,
    openChat,
    openWorkflowCanvas,
    pinChat,
    renameChat,
    shareChat,
    deleteChat,
  };
}
